import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import AgGridPagination from "../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Balances = {
  available: string;
};

type RowData = {
  id: string;
  merchant: Merchant;
  suba_name: string;
  balances: Balances;
};

const AgentList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const MerchantRenderer = (params: ICellRendererParams) => (
    <>{params.data.merchant?.name}</>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <Tooltip title="View More Details" arrow placement="right">
      <div>
        <IconButton
          component={Link}
          to={`/${userRole}/agents/${params.data.id}`}
          id={"button-detail-" + params.data?.id}
          size="small"
        >
          <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "merchant",
      headerName: "Merchant",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: MerchantRenderer,
    },
    {
      field: "suba_name",
      headerName: "Agent",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 80,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <Box className="card mt-3 w-full">
      <div style={{ width: "100%" }}>
        <AgGridReact
          theme={themeAlpine}
          rowData={isLoading ? [] : tableData}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          rowHeight={48}
          headerHeight={44}
          domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressPaginationPanel={true}
          loading={isLoading}
          loadingOverlayComponent={CustomLoadingOverlay}
          overlayNoRowsTemplate="No data available"
        />
      </div>

      <AgGridPagination
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPaginationChange={setPaginationModel}
        hidden={hidePagination}
      />
    </Box>
  );
};

export default AgentList;
